import { format, parseISO } from 'date-fns';
import {
  AlexaVisibilityQuerySet,
  AlexaVisibilityResult,
  AlexaVisibilityRun
} from '@blisspointmedia/bpm-types/dist/AlexaVisibility';
import { toPrettyNumber } from '@src/utils/performance-utils';
import {
  CITATION_TYPE_LABELS,
  TOP_BRANDS_LIMIT,
  TOP_PRODUCTS_LIMIT,
  TOP_SOURCES_LIMIT,
  EXPORT_PRODUCTS_PER_SOURCE
} from './alexa-visibility-constants';

const DATE_KEY_FORMAT = 'yyyy-MM-dd';

export const CategoryHeaderStatus = {
  WON: 'won',
  OPPORTUNITY: 'opportunity'
} as const;
export type CategoryHeaderStatusType =
  (typeof CategoryHeaderStatus)[keyof typeof CategoryHeaderStatus];

export interface AttributeCount {
  attribute: string;
  count: number;
}

export interface CategoryHeaderStat {
  header: string;
  brand: string;
  count: number;
  status: CategoryHeaderStatusType;
}

export interface AvgPositionWithRankedShare {
  avgPosition: number | undefined;
  percentRankedInTop3: number;
}

export interface CountedItem {
  label: string;
  count: number;
}

/** Lowercased, trimmed brand name, for matching and lookups. */
export const normalizeBrand = (value: string): string => value.trim().toLowerCase();

/** Whether two brand names refer to the same brand, ignoring case and surrounding whitespace. */
export const isSameBrand = (a: string, b: string): boolean =>
  normalizeBrand(a) === normalizeBrand(b);

// Compares the timestamp's local calendar day against `yyyy-MM-dd` bounds (inclusive). An empty
// bound is open-ended, matching how the Trends tab has always bucketed results by day.
const isWithinDateRange = (timestamp: string, dateFrom: string, dateTo: string): boolean => {
  const day = format(parseISO(timestamp), DATE_KEY_FORMAT);
  if (dateFrom && day < dateFrom) return false;
  if (dateTo && day > dateTo) return false;
  return true;
};

/**
 * Results scraped on or between `dateFrom` and `dateTo` (`yyyy-MM-dd`, inclusive). With no bounds
 * every result from every run is returned, so callers aggregate across all runs by default and
 * across only the matching runs once a range is chosen.
 */
export const filterResultsByDateRange = (
  results: AlexaVisibilityResult[],
  dateFrom: string,
  dateTo: string
): AlexaVisibilityResult[] => {
  if (!dateFrom && !dateTo) return results;
  return results.filter(result => isWithinDateRange(result.createdAt, dateFrom, dateTo));
};

/** Runs started on or between `dateFrom` and `dateTo` (`yyyy-MM-dd`, inclusive); no bounds = all. */
export const filterRunsByDateRange = (
  runs: AlexaVisibilityRun[],
  dateFrom: string,
  dateTo: string
): AlexaVisibilityRun[] => {
  if (!dateFrom && !dateTo) return runs;
  return runs.filter(run => isWithinDateRange(run.startedAt, dateFrom, dateTo));
};

// Typed empty fallback for `classification.competitorAttributes` -- shared so
// `?? {}` at call sites doesn't widen `Object.entries` to `unknown` values.
export const EMPTY_COMPETITOR_ATTRIBUTES: Record<string, string[]> = {};

/** Buckets `items` into a `Map` keyed by `keyFn(item)`, preserving encounter order. */
export const groupBy = <T, K>(items: T[], keyFn: (item: T) => K): Map<K, T[]> => {
  const grouped = new Map<K, T[]>();
  items.forEach(item => {
    const key = keyFn(item);
    const existing = grouped.get(key);
    if (existing) {
      existing.push(item);
    } else {
      grouped.set(key, [item]);
    }
  });
  return grouped;
};

/**
 * Ranks a flat list of labels by frequency: counts occurrences (case-insensitive,
 * first-seen casing wins as the display label), sorts descending by count, and
 * optionally slices to the top `limit`.
 */
export const rankByFrequency = (labels: string[], limit?: number): CountedItem[] => {
  const counts = new Map<string, CountedItem>();
  labels.forEach(label => {
    const key = normalizeBrand(label);
    const existing = counts.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      counts.set(key, { label, count: 1 });
    }
  });

  const ranked = Array.from(counts.values()).sort((a, b) => b.count - a.count);
  return limit !== undefined ? ranked.slice(0, limit) : ranked;
};

/** % of results where the classifier flagged the target brand as mentioned. */
export const getVisibilityScore = (results: AlexaVisibilityResult[]): number => {
  if (results.length === 0) return 0;
  const mentionedCount = results.filter(
    result => result.classification?.targetBrandMentioned
  ).length;
  return (mentionedCount / results.length) * 100;
};

/**
 * Target brand's mentions as a share of every brand mention observed across
 * `classification.brandsMentioned` -- i.e. of all the times any brand (target
 * or competitor) showed up in a response, what fraction were the target brand.
 */
export const getShareOfVoice = (results: AlexaVisibilityResult[], targetBrand: string): number => {
  const normalizedTarget = normalizeBrand(targetBrand);
  let targetMentions = 0;
  let totalMentions = 0;

  results.forEach(result => {
    const brandsMentioned = result.classification?.brandsMentioned ?? [];
    totalMentions += brandsMentioned.length;
    if (brandsMentioned.some(brand => normalizeBrand(brand) === normalizedTarget)) {
      targetMentions += 1;
    }
  });

  return totalMentions > 0 ? (targetMentions / totalMentions) * 100 : 0;
};

/**
 * Mode (most frequent value) of `classification.targetBrandRank` across results
 * where it's set. Ties favor the better (lower/more visible) rank.
 */
export const getVisibilityRank = (results: AlexaVisibilityResult[]): number | undefined => {
  const counts = new Map<number, number>();
  results.forEach(result => {
    const rank = result.classification?.targetBrandRank;
    if (typeof rank === 'number') {
      counts.set(rank, (counts.get(rank) ?? 0) + 1);
    }
  });

  if (counts.size === 0) return undefined;

  let modeRank: number | undefined;
  let modeCount = 0;
  Array.from(counts.keys())
    .sort((a, b) => a - b)
    .forEach(rank => {
      const count = counts.get(rank) ?? 0;
      if (count > modeCount) {
        modeCount = count;
        modeRank = rank;
      }
    });

  return modeRank;
};

export interface OverviewKpis {
  visibilityScore: number;
  shareOfVoice: number;
  typicalRank: number | undefined;
  totalResults: number;
  brandsCompared: number;
  strongCitations: number;
  strongCitationsPercent: number;
}

/**
 * Every Query Overview KPI for one set of results. The cards compute the pooled total and a single
 * run through this same function so the two numbers are always calculated identically.
 */
export const getOverviewKpis = (
  results: AlexaVisibilityResult[],
  targetBrand: string
): OverviewKpis => {
  const brands = new Set<string>();
  results.forEach(result => {
    result.classification?.brandsMentioned?.forEach(brand => brands.add(normalizeBrand(brand)));
  });
  const strongCitations = results.filter(
    result => result.classification?.citationStrength === 'strong'
  ).length;

  return {
    visibilityScore: getVisibilityScore(results),
    shareOfVoice: getShareOfVoice(results, targetBrand),
    typicalRank: getVisibilityRank(results),
    totalResults: results.length,
    brandsCompared: brands.size,
    strongCitations,
    strongCitationsPercent: results.length > 0 ? (strongCitations / results.length) * 100 : 0
  };
};

// `partial` means some queries failed but the run still produced results.
const FINISHED_RUN_STATUSES = new Set(['succeeded', 'partial']);

/**
 * Results from the most recent finished run among `results`, to sit beside the pooled numbers.
 * Undefined when there is nothing meaningful to compare: fewer than two runs contributed results
 * (the pooled numbers would just equal the last run) or none of them has finished.
 */
export const getLastRunResults = (
  results: AlexaVisibilityResult[],
  runs: AlexaVisibilityRun[]
): AlexaVisibilityResult[] | undefined => {
  const runIdsWithResults = new Set(results.map(result => result.runId));
  if (runIdsWithResults.size < 2) return undefined;

  const [lastRun] = runs
    .filter(run => FINISHED_RUN_STATUSES.has(run.status) && runIdsWithResults.has(run.id))
    .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());

  return lastRun ? results.filter(result => result.runId === lastRun.id) : undefined;
};

/**
 * `avgPosition` averages `targetBrandRank` only over results where it's set
 * (a rank only exists when the brand cracked the top 3). `percentRankedInTop3`
 * is the percentage of *all* results -- ranked or not -- that achieved a rank,
 * and must always be shown alongside `avgPosition` so a brand ranked #1 once
 * out of 20 runs doesn't read as "Avg Position: 1.0" with no context that it
 * was only ranked in 1 of those 20.
 */
export const getAvgPositionWithRankedShare = (
  results: AlexaVisibilityResult[]
): AvgPositionWithRankedShare => {
  const rankedResults = results.filter(
    result => typeof result.classification?.targetBrandRank === 'number'
  );

  const avgPosition =
    rankedResults.length > 0
      ? rankedResults.reduce(
          (sum, result) => sum + (result.classification?.targetBrandRank ?? 0),
          0
        ) / rankedResults.length
      : undefined;

  const percentRankedInTop3 =
    results.length > 0 ? (rankedResults.length / results.length) * 100 : 0;

  return { avgPosition, percentRankedInTop3 };
};

/**
 * Merges attributes that differ only in case (e.g. "Personalized Coaching" and
 * "personalized coaching") into one entry, combining their counts. The first
 * casing encountered for a given attribute becomes the canonical display label.
 */
export const normalizeAttributeCasing = (attributes: string[]): AttributeCount[] => {
  const byKey = new Map<string, AttributeCount>();

  attributes.forEach(rawAttribute => {
    const trimmed = rawAttribute.trim();
    if (!trimmed) return;
    const key = trimmed.toLowerCase();
    const existing = byKey.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      byKey.set(key, { attribute: trimmed, count: 1 });
    }
  });

  return Array.from(byKey.values()).sort((a, b) => b.count - a.count);
};

export interface AttributeShare extends AttributeCount {
  // Share of the brand's attribute citations, 0-100.
  percent: number;
}

/** Each attribute's share of all the attribute citations given, in the order given. */
export const getAttributeShares = (attributes: AttributeCount[]): AttributeShare[] => {
  const total = attributes.reduce((sum, { count }) => sum + count, 0);
  return attributes.map(attribute => ({
    ...attribute,
    percent: total > 0 ? (attribute.count / total) * 100 : 0
  }));
};

/**
 * Which brands get an attribute chart: the target brand first, then the competitors Alexa mentions
 * most (`competitorOrder`, so the charts match the Query Overview), then any other brand with
 * attributes by citation volume, up to `limit`. Brands with no attribute citations are skipped.
 */
export const selectAttributeBrands = (
  citationsByBrand: Map<string, number>,
  competitorOrder: string[],
  targetBrand: string,
  limit = TOP_BRANDS_LIMIT
): string[] => {
  const brands = Array.from(citationsByBrand.keys());
  const ordered: string[] = [];
  const add = (brand: string | undefined) => {
    if (brand !== undefined && !ordered.includes(brand)) {
      ordered.push(brand);
    }
  };

  add(brands.find(brand => isSameBrand(brand, targetBrand)));
  competitorOrder.forEach(competitor => add(brands.find(brand => isSameBrand(brand, competitor))));
  brands
    .slice()
    .sort((a, b) => (citationsByBrand.get(b) ?? 0) - (citationsByBrand.get(a) ?? 0))
    .forEach(add);

  return ordered.slice(0, limit);
};

/** "1,150 (16%)": an attribute's citations and share, as drawn at the end of its bar. */
export const formatAttributeShareLabel = ({ count, percent }: AttributeShare): string =>
  `${toPrettyNumber(count)} (${percent.toFixed(0)}%)`;

/** One row per attribute (not just the top ones), for a brand's "Download all" export. */
export const buildAttributeExportRows = (attributes: AttributeCount[]): Record<string, unknown>[] =>
  getAttributeShares(attributes).map(({ attribute, count, percent }, index) => ({
    Rank: index + 1,
    Attribute: attribute,
    Citations: count,
    '% of Citations': Number(percent.toFixed(1))
  }));

/**
 * Groups `classification.categoryHeaders` by header, and for each header picks
 * the most frequently attributed brand as the header's "holder" -- marking it
 * `won` when that's the target brand and `opportunity` when it's a competitor.
 */
export const getCategoryHeaderStats = (
  results: AlexaVisibilityResult[],
  targetBrand: string
): CategoryHeaderStat[] => {
  const headerBrandCounts = new Map<string, Map<string, number>>();

  results.forEach(result => {
    result.classification?.categoryHeaders?.forEach(({ header, brand }) => {
      if (!header || !brand) return;
      const brandCounts = headerBrandCounts.get(header) ?? new Map<string, number>();
      brandCounts.set(brand, (brandCounts.get(brand) ?? 0) + 1);
      headerBrandCounts.set(header, brandCounts);
    });
  });

  const normalizedTarget = normalizeBrand(targetBrand);

  return Array.from(headerBrandCounts.entries()).map(([header, brandCounts]) => {
    const [topBrand, topCount] = Array.from(brandCounts.entries()).reduce((best, entry) =>
      entry[1] > best[1] ? entry : best
    );
    return {
      header,
      brand: topBrand,
      count: topCount,
      status:
        normalizeBrand(topBrand) === normalizedTarget
          ? CategoryHeaderStatus.WON
          : CategoryHeaderStatus.OPPORTUNITY
    };
  });
};

export interface ShareItem {
  label: string;
  count: number;
  percent: number;
}

/**
 * How many results carry each citation type, and what percent of all results that is. A result can
 * carry several types, so the percentages can add up to more than 100. Known types always appear
 * (in display order, even at zero); any type the classifier adds later follows, most common first.
 */
export const getCitationTypeShares = (results: AlexaVisibilityResult[]): ShareItem[] => {
  const counts = new Map<string, number>();
  results.forEach(result => {
    // A type listed twice on one result still only counts that result once.
    new Set(result.classification?.citationTypes ?? []).forEach(type => {
      counts.set(type, (counts.get(type) ?? 0) + 1);
    });
  });

  const knownTypes = Object.keys(CITATION_TYPE_LABELS);
  const extraTypes = Array.from(counts.keys())
    .filter(type => !knownTypes.includes(type))
    .sort((a, b) => (counts.get(b) ?? 0) - (counts.get(a) ?? 0));

  return [...knownTypes, ...extraTypes].map(type => {
    const count = counts.get(type) ?? 0;
    return {
      label: type in CITATION_TYPE_LABELS ? CITATION_TYPE_LABELS[type] : type,
      count,
      percent: results.length > 0 ? (count / results.length) * 100 : 0
    };
  });
};

/**
 * The most-mentioned brands across results, each with its share of ALL brand mentions, not just
 * the ones shown. The target brand is always included so it can be compared with its competitors:
 * if it isn't among the top `limit` it takes the last slot (it ranks no higher than that slot, so
 * the bars stay in order), and with no mentions of its own it shows at zero. When nobody is
 * mentioned at all there is nothing to compare, so the result is empty.
 */
export const getBrandMentionShares = (
  results: AlexaVisibilityResult[],
  targetBrand: string,
  limit = TOP_BRANDS_LIMIT
): ShareItem[] => {
  const mentions = results.flatMap(result => result.classification?.brandsMentioned ?? []);
  const ranked = rankByFrequency(mentions);
  const top = ranked.slice(0, limit);

  const targetShown = top.some(({ label }) => isSameBrand(label, targetBrand));
  const shown =
    targetShown || ranked.length === 0 || !targetBrand.trim()
      ? top
      : [
          ...top.slice(0, Math.max(0, limit - 1)),
          ranked.find(({ label }) => isSameBrand(label, targetBrand)) ?? {
            label: targetBrand,
            count: 0
          }
        ];

  return shown.map(({ label, count }) => ({
    label,
    count,
    percent: (count / mentions.length) * 100
  }));
};

export interface ProductShare extends ShareItem {
  // The brand the product belongs to, when one of the brands Alexa mentioned prefixes its name.
  brand?: string;
}

/**
 * The brand a product name belongs to: the longest mentioned brand the name starts with
 * ("Oura Ring 4 - $349" -> "Oura"), compared case-insensitively on whole words.
 */
export const findProductBrand = (product: string, brands: string[]): string | undefined => {
  const name = normalizeBrand(product);
  return brands
    .filter(brand => {
      const prefix = normalizeBrand(brand);
      return prefix !== '' && (name === prefix || name.startsWith(`${prefix} `));
    })
    .sort((a, b) => b.length - a.length)[0];
};

/** Every distinct brand Alexa mentioned across the results, most mentioned first. */
export const getMentionedBrands = (results: AlexaVisibilityResult[]): string[] =>
  rankByFrequency(results.flatMap(result => result.classification?.brandsMentioned ?? [])).map(
    ({ label }) => label
  );

/** The products in one response's top 3, each counted once (casing and blanks ignored). */
export const getResultProducts = (result: AlexaVisibilityResult): string[] =>
  Array.from(
    new Map(
      (result.classification?.top3Recommendations ?? [])
        .filter(product => product.trim() !== '')
        .map(product => [normalizeBrand(product), product])
    ).values()
  );

/**
 * Every product Alexa recommended (each response's top 3), most recommended first, with its
 * share of ALL product recommendations. Counts a product once per response it appears in.
 */
export const getRecommendedProducts = (results: AlexaVisibilityResult[]): ProductShare[] => {
  const brands = getMentionedBrands(results);
  const recommendations = results.flatMap(getResultProducts);

  return rankByFrequency(recommendations).map(({ label, count }) => ({
    label,
    count,
    percent: (count / recommendations.length) * 100,
    brand: findProductBrand(label, brands)
  }));
};

/** The `limit` most recommended products across all results. */
export const getTopRecommendedProducts = (
  results: AlexaVisibilityResult[],
  limit = TOP_PRODUCTS_LIMIT
): ProductShare[] => getRecommendedProducts(results).slice(0, limit);

/** One row per product mentioned (not just the top ones), for the "Products" Excel sheet. */
export const buildProductsExportRows = (
  results: AlexaVisibilityResult[]
): Record<string, unknown>[] =>
  getRecommendedProducts(results).map(({ label, brand, count, percent }, index) => ({
    Rank: index + 1,
    Product: label,
    Brand: brand ?? '',
    Recommendations: count,
    '% of Recommendations': Number(percent.toFixed(1))
  }));

/**
 * Every brand mentioned except the target, most mentioned first. Colors are handed out by position
 * in this list, so a brand keeps the same color in every chart on the page.
 */
export const rankCompetitorBrands = (
  results: AlexaVisibilityResult[],
  targetBrand: string
): string[] =>
  rankByFrequency(results.flatMap(result => result.classification?.brandsMentioned ?? []))
    .map(({ label }) => label)
    .filter(label => !isSameBrand(label, targetBrand));

/** Top competing (non-target) brands by total mention count, across all results. */
export const getTopCompetingBrands = (
  results: AlexaVisibilityResult[],
  targetBrand: string,
  limit = 10
): CountedItem[] => {
  const normalizedTarget = normalizeBrand(targetBrand);
  const competitorMentions = results
    .flatMap(result => result.classification?.brandsMentioned ?? [])
    .filter(brand => normalizeBrand(brand) !== normalizedTarget);

  return rankByFrequency(competitorMentions, limit);
};

export interface CitationSourceStat {
  source: string;
  // How many times results cite the source (case-insensitive).
  citations: number;
  // Products recommended in those same responses, most recommended first.
  products: ProductShare[];
}

/**
 * Every citation source, most cited first, with the products Alexa recommended in the responses
 * that cite it. The data doesn't say which source backs which product, so a source is credited
 * with the top 3 of every response that cites it (once per response, however often it is listed).
 */
export const getCitationSourceStats = (results: AlexaVisibilityResult[]): CitationSourceStat[] => {
  const brands = getMentionedBrands(results);
  const bySource = new Map<string, { source: string; citations: number; products: string[] }>();
  results.forEach(result => {
    const products = getResultProducts(result);
    const seenInResult = new Set<string>();
    (result.classification?.citationSources ?? [])
      .filter(source => source.trim() !== '')
      .forEach(source => {
        const key = normalizeBrand(source);
        const entry = bySource.get(key) ?? { source, citations: 0, products: [] };
        entry.citations += 1;
        if (!seenInResult.has(key)) {
          entry.products.push(...products);
          seenInResult.add(key);
        }
        bySource.set(key, entry);
      });
  });

  return Array.from(bySource.values())
    .sort((a, b) => b.citations - a.citations)
    .map(({ source, citations, products }) => ({
      source,
      citations,
      products: rankByFrequency(products).map(({ label, count }) => ({
        label,
        count,
        percent: (count / products.length) * 100,
        brand: findProductBrand(label, brands)
      }))
    }));
};

/** The `limit` most cited sources. */
export const getTopCitationSources = (
  results: AlexaVisibilityResult[],
  limit = TOP_SOURCES_LIMIT
): CitationSourceStat[] => getCitationSourceStats(results).slice(0, limit);

/** One row per source (not just the top ones), for the "Sources" Excel sheet. */
export const buildCitationSourcesExportRows = (
  results: AlexaVisibilityResult[]
): Record<string, unknown>[] =>
  getCitationSourceStats(results).map(({ source, citations, products }, index) => ({
    Rank: index + 1,
    Source: source,
    Citations: citations,
    // Capped so a heavily cited source can't overflow Excel's cell limit; the "Source Products"
    // sheet has every product.
    'Products Recommended': products
      .slice(0, EXPORT_PRODUCTS_PER_SOURCE)
      .map(({ label, count }) => `${label} (${count})`)
      .join('; ')
  }));

/** One row per source and product recommended alongside it, for the "Source Products" sheet. */
export const buildCitationSourceProductExportRows = (
  results: AlexaVisibilityResult[]
): Record<string, unknown>[] =>
  getCitationSourceStats(results).flatMap(({ source, products }) =>
    products.map(({ label, brand, count }) => ({
      Source: source,
      Product: label,
      Brand: brand ?? '',
      'Times Recommended': count
    }))
  );

const joinList = (values?: string[]): string => (values ?? []).join('; ');

/** One row per scan (run), newest first, for the "Runs" sheet of the Excel export. */
export const buildRunsExportRows = (runs: AlexaVisibilityRun[]): Record<string, unknown>[] =>
  runs.map(run => ({
    'Run #': run.runNumber,
    Brand: run.brand,
    'Query Set': run.querySet ?? '',
    Status: run.status,
    'Total Queries': run.totalQueries,
    'Completed Queries': run.completedQueries,
    'Failed Queries': run.failedQueries,
    'Triggered By': run.triggeredBy ?? '',
    'Started At': run.startedAt,
    'Completed At': run.completedAt ?? '',
    Error: run.error ?? ''
  }));

/**
 * One row per scraped query across every run, with the run it belongs to and the full
 * classification detail. Results whose run isn't in `runs` get a blank run number.
 */
export const buildResultsExportRows = (
  results: AlexaVisibilityResult[],
  runs: AlexaVisibilityRun[]
): Record<string, unknown>[] => {
  const runNumberById = new Map(runs.map(run => [run.id, run.runNumber]));
  return results.map(result => {
    const { classification } = result;
    return {
      'Run #': runNumberById.get(result.runId) ?? '',
      'Scraped At': result.createdAt,
      Query: result.query,
      Category: result.category ?? '',
      Persona: result.persona ?? '',
      Mentioned: classification?.targetBrandMentioned ? 'Yes' : 'No',
      Rank: classification?.targetBrandRank ?? '',
      Position: classification?.targetBrandPosition ?? '',
      'Citation Strength': classification?.citationStrength ?? '',
      'Citation Types': joinList(classification?.citationTypes),
      'Competitive Framing': classification?.competitiveFraming ?? '',
      'Response Format': classification?.responseFormat ?? '',
      'Brands Mentioned': joinList(classification?.brandsMentioned),
      'Recommended Brands': joinList(classification?.recommendedBrands),
      'Top 3 Recommendations': joinList(classification?.top3Recommendations),
      'Key Attributes Cited': joinList(classification?.keyAttributesCited),
      'Citation Sources': joinList(classification?.citationSources),
      'Timed Out': result.timedOut ? 'Yes' : 'No',
      'Possibly Truncated': result.possiblyTruncated ? 'Yes' : 'No',
      Error: result.error ?? '',
      Response: result.response ?? ''
    };
  });
};

/**
 * The `/alexa-visibility-scan` command that runs a scan for this client, brand and query set from
 * the user's own machine. The brand is always quoted (it can contain spaces); `--set` is omitted
 * for the default of all queries.
 */
export const buildScanCommand = ({
  company,
  brand,
  querySet
}: {
  company: string;
  brand: string;
  querySet: AlexaVisibilityQuerySet;
}): string => {
  const parts = ['/alexa-visibility-scan', company];
  if (brand) {
    parts.push('--brand', `"${brand.replace(/(["\\$`])/g, '\\$1')}"`);
  }
  if (querySet !== 'all') {
    parts.push('--set', querySet);
  }
  return parts.join(' ');
};
